/**
 * Payroll PCS — code table browser (read for finance; edit for superadmin).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import { payrollAPI } from '../../services/api';

const TABS = [
  { key: 'company', label: 'Company', load: () => payrollAPI.listCompanyCodes(), cols: ['code', 'name', 'cost_center'] },
  { key: 'cost', label: 'Cost Center', load: () => payrollAPI.listCostCenterCodes(), cols: ['code', 'description', 'matching_company'] },
  { key: 'cfr', label: 'CFR', load: () => payrollAPI.listCFRCodes(), cols: ['code', 'description'] },
  { key: 'program', label: 'Program', load: () => payrollAPI.listProgramCodes(), cols: ['program', 'description', 'invalid_flag'] },
  { key: 'check', label: 'Check Type', load: () => payrollAPI.listCheckTypeCodes(), cols: ['code', 'description'] },
];

const PayrollCodesPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await TABS[tab].load();
        if (!cancelled) setRows(res.data.results || res.data || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load codes');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const cols = TABS[tab].cols;

  return (
    <>
      <AppHeader backTo="/payroll" />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom>
          Payroll code tables
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seeded from ADP2PayComRequirements. Read-only for finance
          {user?.role === 'superadmin' ? '; superadmin may edit via API/admin.' : '.'}
        </Typography>

        <Paper>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            {TABS.map((t) => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {cols.map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {cols.map((c) => (
                      <TableCell key={c}>
                        {typeof row[c] === 'boolean' ? (row[c] ? 'Yes' : 'No') : row[c] ?? '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {rows.length} rows
              </Typography>
            </Box>
          </TableContainer>
        )}
      </Container>
    </>
  );
};

export default PayrollCodesPage;
